import {useCallback, useState} from 'react';

import classNames from 'classnames';

import {colors} from 'shells/colors/colors';
import {Illustration} from 'shells/illustrations/illustration';

import type {CoordinatesFragment, LocationWeatherFragment} from 'nutshell-graphql-types';

import type {PeepType} from '../../../types';

import {LOCATION_ACCURACY} from './consts';
import {getGoogleMapsUrl} from './helpers';
import {MaplibreMapLayer} from './maplibre-map-layer';
import {WeatherOverlay} from './weather-overlay';

import styles from './peep-map.module.css';

interface Props {
    peepType: PeepType;
    locationCoordinates: CoordinatesFragment | null | undefined;
    locationWeather: LocationWeatherFragment | null | undefined;
}

export function PeepMap(props: Props) {
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const [isSuperZoom, setIsSuperZoom] = useState<boolean>(false);
    const [hasWebGLError, setHasWebGLError] = useState<boolean>(false);

    const latitude = Number(props.locationCoordinates?.latitude);
    const longitude = Number(props.locationCoordinates?.longitude);
    const hasCoordinates =
        !hasWebGLError && Number.isFinite(latitude) && Number.isFinite(longitude);
    const emptyIllustrationSrc =
        props.peepType === 'account'
            ? 'peep-pages/account-empty-map'
            : 'peep-pages/contact-empty-map';

    const mapEmptyState = {
        backgroundColor: props.peepType === 'account' ? colors.plumBg : colors.blueBg,
        illustrationSrc: emptyIllustrationSrc,
    };

    const activeLocationAccuracy = hasCoordinates
        ? props.locationCoordinates?.accuracy ?? LOCATION_ACCURACY.unknown
        : LOCATION_ACCURACY.unknown;

    const hasGoogleMapsLink = activeLocationAccuracy > LOCATION_ACCURACY.subregion;
    const hasWeatherData = Boolean(
        props.locationWeather &&
            ((props.locationWeather.temperature ?? '').trim() ||
                (props.locationWeather.localTime ?? '').trim() ||
                (props.locationWeather.location ?? '').trim())
    );

    const bottomBorderColor = props.peepType === 'account' ? colors.company : colors.blue;

    const onWebGLError = useCallback(() => setHasWebGLError(true), []);

    return (
        <div
            className={classNames(styles['container'], {
                [styles['container-short']]: NutClientConfig.hasCoreEntityFullHeightSidebarFF,
                [styles['container-tall']]:
                    hasCoordinates && NutClientConfig.hasCoreEntityFullHeightSidebarFF,
            })}
            style={{borderBottom: `4px solid ${bottomBorderColor}`}}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => {
                setIsHovered(false);
                setIsSuperZoom(false);
            }}
        >
            {hasCoordinates ? (
                <>
                    <MaplibreMapLayer
                        peepType={props.peepType}
                        latitude={latitude}
                        longitude={longitude}
                        locationAccuracy={activeLocationAccuracy}
                        isHovered={isHovered}
                        isSuperZoom={isSuperZoom}
                        showBaseShadow={true}
                        onWebGLError={onWebGLError}
                    />
                    <a
                        className={styles['map-link']}
                        href={hasGoogleMapsLink ? getGoogleMapsUrl(latitude, longitude) : undefined}
                        rel='noopener noreferrer'
                        target='_blank'
                        aria-label='Open location in Google Maps'
                    >
                        <div
                            className={styles['map-zoom-target']}
                            onMouseEnter={() => setIsSuperZoom(true)}
                            onMouseLeave={() => setIsSuperZoom(false)}
                        />
                    </a>
                </>
            ) : (
                <div
                    data-empty-state='true'
                    style={{
                        backgroundColor: mapEmptyState.backgroundColor,
                    }}
                >
                    <Illustration src={emptyIllustrationSrc} />
                </div>
            )}
            {hasWeatherData && props.locationWeather && (
                <WeatherOverlay weather={props.locationWeather} />
            )}
        </div>
    );
}
